<script setup lang="ts">
import { type Ref, computed, onBeforeMount, onMounted, ref, watch } from "vue";
import type { ISwiperConfig } from "~/types/swipe-bet/config";
import type { ISwiperContent } from "~/types/swipe-bet/content";
import type { ISwiperProps, ISwiperSubmit } from "~/types/swipe-bet/swiper";
import SwiperCard from "./SwiperCard.vue";
import { STATUS_CONSTANTS } from "./swiperMethods/statusConstants";
import useSwiperCore from "./swiperMethods/swiperCore";
import useSwiperQueue from "./swiperMethods/swiperQueue";
import { type ISwiperState, initState } from "./swiperMethods/swiperState";
import useSwiperTouchEvents from "./swiperMethods/swiperTouchEvents";
import useSwiperTransitions from "./swiperMethods/swiperTransitions";

const props = withDefaults(defineProps<ISwiperProps>(), {
	allowSuper: true,
	allowDown: false,
	keyName: "key",
	pointerThreshold: 0.5,
	superThreshold: 0.5,
	downThreshold: 0.5,
	sync: false,
	max: 3,
	scaleStep: 0.05,
	offsetY: 0,
	offsetUnit: "px",
});

const queue = computed({
	get: () => {
		return props.modelValue;
	},
	set: (val) => {
		emit("update:modelValue", val);
	},
});

const swiperConfig: Ref<ISwiperConfig> = ref({
	leavedCount: 0,
	hideIndex: 50,
	lastHideIndex: 50,
	hidingKeys: [],
	rewindKeys: [],
	leavingKeys: [],
	size: {
		top: 0,
		width: 0,
		height: 0,
	},
	onceRewindCount: 0,
});

const swiperState: Ref<ISwiperState> = ref(initState());
const isMounted = ref(false);
const list = ref([]);
const root: Ref<HTMLDivElement | null> = ref(null);
const resizeTimer: Ref<NodeJS.Timeout | null> = ref(null);

const emit = defineEmits<{
	submit: [ISwiperSubmit];
	"update:modelValue": [ISwiperContent[]];
}>();

const ratio = computed(() => {
	if (swiperConfig.value.size.width) {
		const { start, move } = swiperState.value;
		const x = move.x - start.x || 0;
		return x / (swiperConfig.value.size.width * 0.5);
	}
	return 0;
});

const pointerOpacity = computed(() => {
	return ratio.value / props.pointerThreshold;
});

const disY = computed(() => {
	if (props.allowSuper || props.allowDown) {
		return swiperState.value.move.y - swiperState.value.start.y;
	}
	return 0;
});

const superOpacity = computed(() => {
	if (!props.allowSuper) {
		return 0;
	}
	const ratio = disY.value / (-props.superThreshold * swiperConfig.value.size.height);
	const pointerOpacityAbs = Math.abs(pointerOpacity.value);
	return ratio > pointerOpacityAbs ? ratio : 0;
});

const downOpacity = computed(() => {
	if (!props.allowDown) {
		return 0;
	}
	const ratio = disY.value / (props.downThreshold * swiperConfig.value.size.height);
	const pointerOpacityAbs = Math.abs(pointerOpacity.value);
	return ratio > pointerOpacityAbs ? ratio : 0;
});

const likeOpacity = computed(() => {
	if (superOpacity.value || downOpacity.value) {
		return 0;
	}
	return pointerOpacity.value;
});

const nopeOpacity = computed(() => {
	return -likeOpacity.value;
});

const getSize = () => {
	if (!root.value) return;

	if (resizeTimer.value) {
		clearInterval(resizeTimer.value);
	}

	resizeTimer.value = setTimeout(() => {
		swiperConfig.value.size = {
			top: root.value?.offsetTop ?? 0,
			width: root.value?.offsetWidth ?? 0,
			height: root.value?.offsetHeight ?? 0,
		};
	}, 300);
};

const handleCardReverted = () => {
	// Reset the status when card animation completes
	if (swiperState.value.status === STATUS_CONSTANTS.REWINDING || swiperState.value.status === STATUS_CONSTANTS.REVERT) {
		swiperState.value.status = STATUS_CONSTANTS.NORMAL;
		swiperState.value.result = null;
		swiperState.value.touchId = null;
	}
};

onBeforeMount(() => {
	list.value = JSON.parse(JSON.stringify(queue.value));
});

onMounted(() => {
	if (!root.value?.offsetWidth || !root.value?.offsetHeight) {
		console.error("Swiper couldn't find root element or element has not height or width");
		return;
	}

	swiperConfig.value.size = {
		top: root.value.offsetTop,
		width: root.value.offsetWidth,
		height: root.value.offsetHeight,
	};
	window.onresize = getSize;
	isMounted.value = true;
});

const { shiftCard, decide, rewind } = useSwiperCore(swiperState, swiperConfig, emit, queue);
const { beforeEnter, leave } = useSwiperTransitions(swiperConfig, props, swiperState);
const { diff } = useSwiperQueue(swiperConfig, props, swiperState, list, queue);
const { start, move, end } = useSwiperTouchEvents(swiperConfig, swiperState, pointerOpacity, superOpacity, downOpacity, shiftCard);

defineExpose({
	decide,
	rewind,
});

watch(
	() => props.modelValue,
	(val: ISwiperContent[]) => {
		const newKeys = val.map((item) => item.swipeBetId);
		const oldKeys = list.value.map((item) => item[props.keyName]);
		diff(newKeys, oldKeys);
	}
);
</script>

<template>
	<div class="relative" ref="root">
		<transition-group
			tag="div"
			class="swiper-inner"
			:css="false"
			@beforeenter="beforeEnter"
			@leave="leave"
			@touchstart="start"
			@touchmove="move"
			@touchend="end"
			@touchcancele="end"
			@mousedown="start"
			@mousemove="move"
			@mouseup="end"
		>
			<template v-for="(item, index) in list">
				<SwiperCard
					v-if="index < max + 1"
					:ready="index === max"
					:key="item[keyName]"
					:data-id="item[keyName]"
					:index="index"
					:swiperState="swiperState"
					:ratio="ratio"
					:tinder-mounted="isMounted"
					:scale-step="scaleStep"
					:offset-y="offsetY"
					:offset-unit="offsetUnit"
					@reverted="handleCardReverted"
				>
					<slot :data="item" :index="index" :status="swiperState.status"></slot>
					<template v-if="index === 0 && swiperState.status !== 2">
						<span class="pointer-wrap nope-pointer-wrap" :style="{ opacity: nopeOpacity }">
							<slot name="nope" :opacity="nopeOpacity" />
						</span>
						<span class="pointer-wrap like-pointer-wrap" :style="{ opacity: likeOpacity }">
							<slot name="like" :opacity="likeOpacity" />
						</span>
					</template>
					<span v-if="swiperState.status === 4" slot="rewind" class="pointer-wrap rewind-pointer-wrap">
						<slot name="rewind" />
					</span>
				</SwiperCard>
			</template>
		</transition-group>
	</div>
</template>

<style>
.swiper {
	-webkit-tap-highlight-color: transparent;
}

.pointer-wrap {
	pointer-events: none;
	transition: opacity 0.2s ease;
}

.tinder-card.nope .nope-pointer-wrap,
.tinder-card.like .like-pointer-wrap,
.tinder-card.super .super-pointer-wrap,
.tinder-card.down .down-pointer-wrap {
	opacity: 1 !important;
}

.tinder-card.nope .rewind-pointer-wrap,
.tinder-card.like .rewind-pointer-wrap,
.tinder-card.super .rewind-pointer-wrap,
.tinder-card.down .rewind-pointer-wrap {
	display: none;
}
</style>
